import React, { useState } from "react";
import moment from "moment";
import { InlineLoading, Select, SelectItem, TableCell, Tooltip } from "carbon-components-react";

import BaseTable from "web_modules/shared/BaseTable";
import { LoadingState } from "underwriter_dashboard/shared/types";
import {
  AppShape,
  TaskOwner,
  TaskShape,
  TaskStage,
  TASK_STATUS_OPTIONS,
} from "@eave/shared/types/api";
import {
  getTasksForApplication,
  updateTaskForApplication,
} from "underwriter_dashboard/shared/Tasks/APIHelpers";

import { newTaskDefaultIndexOrder, getTableHeadersForStage, isComplete } from "./helpers";
import { TaskFormShape } from "./index";
import InlineDateEditingCell from "./InlineDateEditingCell";

interface TasksForRoleProps {
  app: AppShape;
  allTasks: TaskShape[];
  stagesForRole: TaskStage[];
  taskOwner: TaskOwner;

  // shared props
  operators: any;
  setTasks: Function;
  updateLoadingState: Function;
  updateErrorMessage: Function;
  updateTaskFormState: Function;

  // Pass-through functions
  tableProps: Object;
}

const TasksForRole: React.FC<TasksForRoleProps> = ({
  app,
  taskOwner,
  operators,
  allTasks,
  setTasks,
  updateLoadingState,
  stagesForRole,
  updateErrorMessage,
  updateTaskFormState,
  tableProps,
}: TasksForRoleProps) => {
  const [loadingTask, updateLoadingTask] = useState(null);

  const updateFieldInline = (
    task: TaskShape,
    updateField: string,
    updateValue,
    onSuccess?: Function,
  ) => {
    updateLoadingTask(task.id);
    updateTaskForApplication(app.id, task.id, { [updateField]: updateValue })
      .then(() => {
        getTasksForApplication(app.id).then((response: any) => {
          setTasks(response.tasks);
          updateTaskFormState(null);
          updateLoadingState(LoadingState.FINISHED);
          updateLoadingTask(null);

          if (onSuccess) {
            onSuccess();
          }
        });
      })
      .catch(err => {
        updateLoadingState(LoadingState.FINISHED);
        updateErrorMessage(err);
      });
  };

  const updateStatus = (event, task) => {
    if (event.target.value === task.status) {
      return;
    }
    updateFieldInline(task, "status", event.target.value);
  };

  const renderTasksBaseTable = (taskStage: string, tasksInStage: TaskShape[]): JSX.Element => {
    return (
      <BaseTable
        {...tableProps}
        key={taskStage}
        title={taskStage}
        initializeFormState={() => {
          updateTaskFormState({
            stage: taskStage,
            index_order: newTaskDefaultIndexOrder(allTasks, taskStage),
            default_index_order: newTaskDefaultIndexOrder(allTasks, taskStage),
            operator_override_due_date: null,
            owner: taskOwner,
            include_in_queues: true,
            is_critical: false,
            pinned: false,
            blocks_loan_decision: false,
          } as TaskFormShape);
        }}
        headerData={getTableHeadersForStage(taskStage)}
        rawRowData={tasksInStage}
        collapseOverride={tasksInStage.every(t => isComplete(t))}
        updateFormState={data =>
          updateTaskFormState({
            ...data,
            default_index_order: newTaskDefaultIndexOrder(allTasks, taskStage),
          })
        }
        rowData={tasksInStage
          .sort(task => (task.key ? 1 : -1))
          .sort((t1, t2) => {
            if (t1.index_order < t2.index_order) return -1;
            if (t1.index_order > t2.index_order) return 1;
            return 0;
          })
          .map((task: TaskShape) => {
            return {
              ...task,
              id: String(task.id),
              lowOpacity: isComplete(task),
              is_critical: (
                <TableCell
                  style={task.is_critical ? { color: "red", fontWeight: "bold" } : { opacity: 0.7 }}
                >
                  {task.is_critical ? "Yes" : "No"}
                </TableCell>
              ),
              pinned: (
                <TableCell
                  style={task.pinned ? { color: "red", fontWeight: "bold" } : { opacity: 0.7 }}
                >
                  {task.pinned ? "Yes" : "No"}
                </TableCell>
              ),
              [`resolved-due-date-${taskStage}`]: task.resolved_due_date && (
                <InlineDateEditingCell task={task} updateFieldInline={updateFieldInline} />
              ),
              [`category-${taskStage}`]: task.category,
              [`name-${taskStage}`]: task.name,
              [`status-${taskStage}`]: (
                <TableCell key={`${task.id}-status`}>
                  {loadingTask === task.id && (
                    <div
                      style={{
                        position: "absolute",
                        minWidth: "224px",
                        zIndex: 1,
                        paddingTop: "8px",
                        paddingLeft: "6rem",
                      }}
                    >
                      <InlineLoading status={LoadingState.ACTIVE} />
                    </div>
                  )}
                  <>
                    {/* Inline Select for choosing the Task Status */}
                    <Select
                      disabled={loadingTask === task.id}
                      id="task-status-inline"
                      name="status"
                      value={task.status}
                      labelText=""
                      style={{ marginBottom: "10px" }}
                      onBlur={event => updateStatus(event, task)}
                      onChange={event => updateStatus(event, task)}
                      size="sm"
                    >
                      {TASK_STATUS_OPTIONS.map(status => (
                        <SelectItem key={`${status}-${taskStage}`} text={status} value={status} />
                      ))}
                    </Select>
                    <span key={`${task.id}-status-date`}>
                      {`Set by ${
                        (operators &&
                          operators[task.status_updated_by_id] &&
                          operators[task.status_updated_by_id].first_name) ||
                        "System"
                      } on ${moment(task.status_updated_at).format("MM/DD/YYYY")}`}
                    </span>
                    {task.completion_hint && (
                      <Tooltip
                        direction="bottom"
                        tabIndex={0}
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <p>{task.completion_hint}</p>
                      </Tooltip>
                    )}
                  </>
                </TableCell>
              ),
            };
          })}
      />
    );
  };

  return (
    <>
      {renderTasksBaseTable(
        "Pinned tasks",
        allTasks.filter((t: TaskShape) => t.owner === `${taskOwner}` && t.pinned),
      )}
      {stagesForRole.map(taskStage => {
        const tasksInStage = allTasks.filter(
          (t: TaskShape) => t.owner === `${taskOwner}` && t.stage === taskStage && !t.pinned,
        );
        return renderTasksBaseTable(taskStage, tasksInStage);
      })}
    </>
  );
};

export default TasksForRole;
